import React, { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { BottomDrawer } from '../BottomDrawer/BottomDrawer';
import Button from '../_custom/Button/Button';
import LoadingSpinner from '../_custom/LoadingSpinner/LoadingSpinner';
import DocumentViewer from './DocumentViewer';
import DocxDrawer from './DocxDrawer';
import { SheetEditorDrawer } from '../SheetEditor';
import { SPREADSHEET_TYPES, DOCX_TYPES } from './constants';
import { useAddToReportsVisible } from '../Reports/useAddToReportsVisible';
import { classifyAddable, addFileUrlToReports } from '../Reports/addFileToReports';
import { showAddedToReportsToast } from '../Reports/addedToReportsToast';
import { useAppSelector } from '../../app/hooks';
import { downloadIconDataRoomHovered } from '../../assets/hostedassets';
import styles from './FileViewerDrawer.module.scss';

interface FileViewerDrawerProps {
    heading: string;
    fileUrl: string;
    mimeType?: string;
    /** Original file name — drives Add-to-Reports classification and the bottom
     *  drawer's title/download name. Defaults to `heading` (callers that only
     *  have a display heading, not a real file name, still classify fine via
     *  `mimeType`). */
    fileName?: string;
    downloading?: boolean;
    downloadLabel?: string;
    onDownload: () => void;
    onClose: () => void;
    /** Overrides the org id used for Add-to-Reports (default: resolved from the
     *  route's `:id` param). Callers outside a per-org route — e.g. the global
     *  Magic Upload drawer — pass the file's own tagged org here instead, since
     *  the route param may be absent or belong to a different org. */
    assesseeOrgId?: string;
    /** Optional extra content rendered left of the Download button in the drawer header. */
    renderHeaderExtra?: React.ReactNode;
}

/**
 * Shared preview drawer used by the ~8 dashboard preview sites, the dataroom
 * (File) and the GST Analysis report. Dispatches every mime type to the same
 * BottomDrawer surface the rest of the app uses — spreadsheets/docx to the
 * editable SheetEditorDrawer/DocxDrawer, everything else (pdf/image/unknown)
 * to a BottomDrawer hosting the read-only DocumentViewer. No side drawer
 * remains anywhere in this component. Add-to-Reports on the bottom drawers is
 * gated by `useAddToReportsVisible` plus an org id — the `assesseeOrgId` prop
 * when the caller passes one (e.g. Magic Upload, keyed off the file's own
 * tagged org), else resolved from the route (`useParams().id` — the same param
 * the Dashboard pages are keyed on); when no org id is resolvable (e.g. Data
 * Room's pdf/image-only usage), the gate stays off rather than crashing.
 */
const FileViewerDrawer: React.FC<FileViewerDrawerProps> = ({
    heading,
    fileUrl,
    mimeType,
    fileName,
    downloading = false,
    downloadLabel = 'Download',
    onDownload,
    onClose,
    assesseeOrgId: assesseeOrgIdOverride,
    renderHeaderExtra,
}) => {
    const resolvedFileName = fileName ?? heading;
    const params = useParams();
    const assesseeOrgId = assesseeOrgIdOverride ?? params.id;
    const user = useAppSelector((state) => state.user.userData);
    const addToReportsVisible = useAddToReportsVisible();
    const [addingToReports, setAddingToReports] = useState(false);
    const [addedToReports, setAddedToReports] = useState(false);
    const isMountedRef = useRef(true);
    useEffect(
        () => () => {
            isMountedRef.current = false;
        },
        [],
    );

    const canAddToReports = addToReportsVisible && Boolean(assesseeOrgId) && classifyAddable(resolvedFileName, mimeType) !== null;

    const handleAddToReports = canAddToReports
        ? async () => {
              setAddingToReports(true);
              try {
                  await addFileUrlToReports({
                      fileUrl,
                      fileName: resolvedFileName,
                      fileType: mimeType,
                      assesseeOrgId: assesseeOrgId as string,
                      investorOrgId: user?.investorOrgId,
                      createdBy: user?.primaryEmail,
                  });
                  showAddedToReportsToast(
                      () => window.open(`/investor/app/reports/${assesseeOrgId}`, '_blank', 'noopener,noreferrer'),
                      () => {
                          if (isMountedRef.current) setAddedToReports(false);
                      },
                  );
                  setAddedToReports(true);
              } catch (err) {
                  console.error('Failed to add file to Reports:', err instanceof Error ? err.message : 'unknown error');
                  toast.error('Failed to add this file to Reports. Please try again.');
              } finally {
                  setAddingToReports(false);
              }
          }
        : undefined;

    if (mimeType && SPREADSHEET_TYPES.includes(mimeType)) {
        return (
            <SheetEditorDrawer
                open
                onClose={onClose}
                fileName={resolvedFileName}
                fileUrl={fileUrl}
                mimeType={mimeType}
                onAddToReports={handleAddToReports}
                addingToReports={addingToReports}
                addedToReports={addedToReports}
            />
        );
    }

    if (mimeType && DOCX_TYPES.includes(mimeType)) {
        return (
            <DocxDrawer
                open
                onClose={onClose}
                fileName={resolvedFileName}
                fileUrl={fileUrl}
                onDownload={onDownload}
                downloading={downloading}
                onAddToReports={handleAddToReports}
                addingToReports={addingToReports}
                addedToReports={addedToReports}
            />
        );
    }

    return (
        <BottomDrawer
            open
            onClose={onClose}
            title={heading}
            headerRight={
                <div className={styles.headerActions}>
                    {renderHeaderExtra}
                    <Button
                        type="button"
                        aria-label={`Download ${heading}`}
                        text={downloadLabel}
                        className={styles.downloadBtn}
                        icon={
                            downloading ? (
                                <LoadingSpinner color="#6021B3" style={{ margin: '0', padding: '0' }} />
                            ) : (
                                downloadIconDataRoomHovered
                            )
                        }
                        iconStyles={{ height: '0.9375rem' }}
                        onClick={(e) => {
                            e.stopPropagation();
                            onDownload();
                        }}
                    />
                </div>
            }
            className={styles.previewDrawer}
            bodyClassName={styles.previewBody}
            height="100vh"
        >
            <DocumentViewer fileUrl={fileUrl} mimeType={mimeType} />
        </BottomDrawer>
    );
};

export default FileViewerDrawer;
